import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Info } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";

interface RecalculationQueueBannerProps {
  organizationId?: string;
  /** Migration types the pending save will recalculate. */
  affectedTypes: MigrationType[];
}

const formatElapsed = (lockedAt: string | null): string => {
  if (!lockedAt) return "";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(lockedAt).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s ago`;
};

/**
 * Live migration-queue status shown inside the save-confirmation modal. Only mounts while the modal
 * is open (it is rendered as the modal's description), so it polls only when the operator is deciding
 * to save. Highlights when a recalculation is already running for one of the affected migration types.
 */
const RecalculationQueueBanner: React.FC<RecalculationQueueBannerProps> = ({
  organizationId,
  affectedTypes,
}) => {
  const organizationsRepository = new OrganizationsRepository();

  const statusQuery = useQuery({
    queryKey: [QUERY_KEYS.MIGRATION_QUEUE_STATUS, organizationId],
    queryFn: () => organizationsRepository.getMigrationQueueStatus(organizationId!),
    enabled: !!organizationId && affectedTypes.length > 0,
    refetchInterval: (query) =>
      query.state.data?.some((s) => s.isRunning) ? 5000 : 15000,
  });

  const runningAffected = (statusQuery.data ?? []).filter(
    (s) => affectedTypes.includes(s.migrationType) && s.isRunning,
  );

  if (runningAffected.length === 0) {
    return (
      <Alert>
        <Info />
        <AlertTitle>No recalculation currently running</AlertTitle>
        <AlertDescription>
          Saving will start a recalculation for: {affectedTypes.map((t) => MigrationTypeLabels[t]).join(", ")}.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="border-amber-500/50 text-amber-900 dark:text-amber-200 [&>svg]:text-amber-600">
      <Loader2 className="animate-spin" />
      <AlertTitle>A recalculation is already running</AlertTitle>
      <AlertDescription className="text-amber-900/90 dark:text-amber-200/90">
        <ul className="list-disc pl-4 space-y-1">
          {runningAffected.map((s) => (
            <li key={s.migrationType}>
              {MigrationTypeLabels[s.migrationType]}
              {s.lockedAt ? ` — started ${formatElapsed(s.lockedAt)}` : ""}
            </li>
          ))}
        </ul>
        Saving now re-triggers the recalculation; it will restart once the current run releases the queue.
      </AlertDescription>
    </Alert>
  );
};

export default RecalculationQueueBanner;
